/**
 * Outbox-based sync engine for Lexibit.
 *
 * Local mutations enqueue an entry in Dexie `outbox`. A debounced flush()
 * processes entries in FIFO order, pushing to Supabase — consecutive entries
 * for the same (table, op) go out as one batched request. On error it stops
 * and retries on the next trigger.
 *
 * Triggers: after enqueue (debounced ~2s), `online` event,
 * `visibilitychange` → visible, after initialSync.
 *
 * All errors are silent (console.warn + status update) — they never break
 * the local-only path. When Supabase is not configured the engine is inert.
 */

import type { Table } from "dexie";
import { db, type OutboxRow, type OutboxTable, type OutboxOp } from "../db";
import { isSupabaseConfigured } from "../supabase";
import { pushDecks, pullDecks, deleteDeckRemote } from "./decks";
import { pushConcepts, pullConcepts, deleteConceptRemote } from "./concepts";
import { pushConceptForms, pullConceptForms, deleteConceptFormRemote } from "./conceptForms";
import { pushCardStates, pullCardStates, deleteCardStateRemote } from "./cardStates";
import { pushReviewEvents, pullReviewEvents } from "./reviewEvents";
import { pushProfile } from "./profile";

export { pushProfile };

// ---------- KV keys ----------

const KV_LAST_SYNC = "sync:lastFlushAt";
const KV_LAST_SYNC_ERROR = "sync:lastError";
const KV_INITIAL_SYNC_PREFIX = "sync:initialDone:";

// ---------- module state ----------

let currentUserId: string | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;
let initialSyncCount = 0;
let syncSuspended = false;

// ---------- public API ----------

export function setSyncUser(userId: string | null): void {
  currentUserId = userId;
  if (userId) syncSuspended = false;
  if (!userId && flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

export function getSyncUser(): string | null {
  return currentUserId;
}

/**
 * Stop every source of remote writes before a destructive data operation.
 * In-flight requests cannot be cancelled safely, so wait for them to settle
 * before the caller deletes server rows.
 */
export async function quiesceSync(): Promise<void> {
  syncSuspended = true;
  currentUserId = null;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (isFlushing || initialSyncCount > 0) {
    await new Promise((resolve) => setTimeout(resolve, 10));
  }
  // initialSync may have scheduled a zero-delay flush just before settling.
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/**
 * Write one outbox entry with dedup logic. Can be called inside an existing
 * Dexie rw-transaction (no scheduleFlush). Used by store mutations that need
 * atomic outbox writes.
 */
export async function enqueueInTx(table: OutboxTable, op: OutboxOp, key: string): Promise<void> {
  if (!isSupabaseConfigured || syncSuspended) return;
  const ts = Date.now();
  // Every pending entry for the key is dropped and one is re-added under a
  // fresh seq rather than overwritten in place: flush() deletes the seq it
  // just processed, so reusing that seq would let an in-flight flush delete
  // this new operation. Deleting *all* matches matters because two racing
  // enqueue() calls can both miss each other's write and leave duplicates.
  await db.outbox.where("[table+key]").equals([table, key]).delete();
  await db.outbox.add({ table, op, key, ts });
}

/**
 * Enqueue a sync operation for (table, key). Deduplicates: any pending entry
 * for the same (table, key) is replaced by this one (moved to the end of the
 * queue under a new seq). No-op when Supabase is not configured.
 */
export async function enqueue(table: OutboxTable, op: OutboxOp, key: string): Promise<void> {
  if (!isSupabaseConfigured || syncSuspended) return;
  await enqueueInTx(table, op, key);
  scheduleFlush();
}

/**
 * Flush pending outbox entries to Supabase. Processes FIFO. Stops on first
 * error and reschedules. Requires userId + navigator.onLine.
 */
export async function flush(): Promise<void> {
  if (!isSupabaseConfigured || syncSuspended) return;
  if (!currentUserId) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  if (isFlushing || initialSyncCount > 0) return;

  const userId = currentUserId;
  isFlushing = true;
  try {
    const entries = await db.outbox.orderBy("seq").toArray();
    for (const batch of groupBatches(entries)) {
      try {
        await processBatch(userId, batch);
      } catch (err) {
        console.warn("[sync] flush error on", batch.entries, err);
        await db.kv.put({ id: KV_LAST_SYNC_ERROR, value: String(err) });
        // Stop processing; schedule a retry in 30s so a stuck entry doesn't
        // wait forever for an external trigger (online/visibility).
        scheduleFlush(30_000);
        return;
      }
      // Entries re-enqueued while the batch was in flight carry a new seq,
      // so deleting the processed seqs cannot drop them.
      await db.transaction("rw", [db.kv, db.outbox], async () => {
        await rememberPushed(userId, batch.table, batch.entries.map((e) => e.key), batch.op);
        await db.outbox.bulkDelete(batch.entries.map((e) => e.seq!));
      });
    }
    // All entries flushed successfully.
    await db.kv.put({ id: KV_LAST_SYNC, value: Date.now() });
    await db.kv.delete(KV_LAST_SYNC_ERROR);
    // Work enqueued during this pass (or by a caller that hit the isFlushing
    // guard) still needs a trip.
    if ((await db.outbox.count()) > 0) scheduleFlush();
  } finally {
    isFlushing = false;
  }
}

/**
 * Sync on sign-in: pull remote data and reconcile clean rows by server revision — this runs on every call, so content added on another device
 * reaches this one. The bulk upload of local content runs only the first
 * time for a userId; the completion flag lives in kv.
 */
export async function initialSync(userId: string): Promise<void> {
  if (!isSupabaseConfigured || syncSuspended) return;
  while (isFlushing || initialSyncCount > 0) {
    await new Promise((resolve) => setTimeout(resolve, 10));
  }
  if (syncSuspended) return;
  initialSyncCount += 1;
  const kvKey = `${KV_INITIAL_SYNC_PREFIX}${userId}`;
  try {
    // 1. Pull all 4 entities
    const [remoteDecks, remoteConcepts, remoteForms, remoteStates, remoteEvents] = await Promise.all([
      pullDecks(userId),
      pullConcepts(userId),
      pullConceptForms(userId),
      pullCardStates(userId),
      pullReviewEvents(userId),
    ]);

    // Reconcile a complete remote snapshot; only pending mutations win locally.
    await reconcile(userId, "decks", db.decks, remoteDecks, (d) => d.id);
    await reconcile(userId, "concepts", db.concepts, remoteConcepts, (c) => c.id);
    await reconcile(userId, "conceptForms", db.conceptForms, remoteForms, (f) => f.key);
    await reconcile(userId, "cardStates", db.cardStates, remoteStates, (s) => s.key);

    await db.transaction("rw", [db.reviewEvents, db.activity], async () => {
      await db.reviewEvents.bulkPut(remoteEvents);
      const totals = new Map<string, { date: string; reviewed: number; correct: number }>();
      for (const event of await db.reviewEvents.toArray()) {
        const day = totals.get(event.reviewDate) ?? { date: event.reviewDate, reviewed: 0, correct: 0 };
        day.reviewed += 1;
        day.correct += event.quality >= 3 ? 1 : 0;
        totals.set(event.reviewDate, day);
      }
      for (const day of totals.values()) {
        const legacy = await db.activity.get(day.date);
        await db.activity.put({ date: day.date, reviewed: Math.max(day.reviewed, legacy?.reviewed ?? 0),
          correct: Math.max(day.correct, legacy?.correct ?? 0) });
      }
    });

    // 3. First run for this user on this device: upload local user content
    //    (non-pack concepts/forms + all decks/states). Rows the cloud already
    //    has are left alone — pushing them would overwrite progress made on
    //    another device with this device's copy.
    const done = await db.kv.get(kvKey);
    if (!done) {
      const [allDecks, allConcepts, allForms, allStates] = await Promise.all([
        db.decks.toArray(),
        db.concepts.toArray(),
        db.conceptForms.toArray(),
        db.cardStates.toArray(),
      ]);

      await pushDecks(userId, notInRemote(allDecks, remoteDecks, (d) => d.id));
      await pushConcepts(userId, notInRemote(allConcepts, remoteConcepts, (c) => c.id));
      await pushConceptForms(userId, notInRemote(allForms, remoteForms, (f) => f.key));
      await pushCardStates(userId, notInRemote(allStates, remoteStates, (s) => s.key));

      for (const [name, keys] of [
        ["decks", allDecks.map((r) => r.id)], ["concepts", allConcepts.map((r) => r.id)],
        ["conceptForms", allForms.map((r) => r.key)], ["cardStates", allStates.map((r) => r.key)],
      ] as [OutboxTable, string[]][]) await rememberPushed(userId, name, keys, "upsert");
      await pushReviewEvents(userId, await db.reviewEvents.toArray());
      await db.kv.put({ id: kvKey, value: { at: Date.now() } });
    }

    await db.kv.put({ id: KV_LAST_SYNC, value: Date.now() });
    await db.kv.delete(KV_LAST_SYNC_ERROR);

    // Flush any outbox entries accumulated during the sync
    scheduleFlush(0);
  } catch (err) {
    console.warn("[sync] initialSync error:", err);
    await db.kv.put({ id: KV_LAST_SYNC_ERROR, value: String(err) });
  } finally {
    initialSyncCount -= 1;
  }
}

// ---------- internal ----------

export function scheduleFlush(delay = 2000): void {
  if (syncSuspended) return;
  if (flushTimer !== null) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush().catch((err) => console.warn("[sync] scheduled flush error:", err));
  }, delay);
}

/** Missing rows mean deletion only for keys previously observed on this account.
 * Never infer deletion for unsynced local/pack rows. Full paginated pulls must
 * succeed before entering here. The transaction protects mutations during pull.
 */
async function reconcile<T extends { syncVersion?: number }>(
  userId: string, name: OutboxTable, table: Table<T, string>,
  rows: T[], keyOf: (row: T) => string,
): Promise<void> {
  await db.transaction("rw", [table, db.kv, db.outbox], async () => {
    const manifestKey = `sync:seen:${userId}:${name}`;
    const previous = (await db.kv.get(manifestKey))?.value as string[] | undefined;
    const pending = new Set((await db.outbox.toArray())
      .filter((e) => e.table === name).map((e) => e.key));
    const remoteKeys = new Set(rows.map(keyOf));
    for (const row of rows) {
      const key = keyOf(row);
      if (pending.has(key)) continue;
      const local = await table.get(key);
      if (!local || (row.syncVersion ?? 0) >= (local.syncVersion ?? 0)) await table.put(row);
    }
    for (const key of previous ?? []) {
      if (!remoteKeys.has(key) && !pending.has(key)) await table.delete(key);
    }
    await db.kv.put({ id: manifestKey, value: [...new Set([
      ...remoteKeys, ...(previous ?? []).filter((key) => pending.has(key)),
    ])] });
  });
}

async function rememberPushed(userId: string, table: OutboxTable, keys: string[], op: OutboxOp): Promise<void> {
  if (table === "reviewEvents") return;
  const id = `sync:seen:${userId}:${table}`;
  const previous = (await db.kv.get(id))?.value as string[] | undefined;
  const seen = new Set(previous ?? []);
  for (const key of keys) { if (op === "upsert") seen.add(key); else seen.delete(key); }
  await db.kv.put({ id, value: [...seen] });
}

function notInRemote<T>(rows: T[], remote: T[], keyOf: (row: T) => string): T[] {
  const remoteKeys = new Set(remote.map(keyOf));
  return rows.filter((row) => !remoteKeys.has(keyOf(row)));
}

type OutboxBatch = { table: OutboxTable; op: OutboxOp; entries: OutboxRow[] };

/** Consecutive entries sharing (table, op) travel in a single request. */
function groupBatches(entries: OutboxRow[]): OutboxBatch[] {
  const batches: OutboxBatch[] = [];
  for (const entry of entries) {
    const last = batches[batches.length - 1];
    if (last && last.table === entry.table && last.op === entry.op) {
      last.entries.push(entry);
    } else {
      batches.push({ table: entry.table, op: entry.op, entries: [entry] });
    }
  }
  return batches;
}

/** Rows deleted locally before the flush are simply skipped. */
function present<T>(rows: (T | undefined)[]): T[] {
  return rows.filter((row): row is T => row !== undefined);
}

async function processBatch(userId: string, batch: OutboxBatch): Promise<void> {
  // Racing enqueue() calls can leave several outbox rows for one key. A
  // batched upsert whose payload repeats a conflict target is rejected by
  // Postgres ("ON CONFLICT DO UPDATE command cannot affect row a second
  // time", SQLSTATE 21000), so each key may appear only once per request.
  const keys = [...new Set(batch.entries.map((e) => e.key))];
  if (batch.op === "delete") {
    // No batch delete primitives exist — one request per key.
    for (const key of keys) {
      await deleteRemote(userId, batch.table, key);
    }
    return;
  }
  switch (batch.table) {
    case "decks": {
      const rows = present(await db.decks.bulkGet(keys));
      if (rows.length > 0) await pushDecks(userId, rows);
      break;
    }
    case "concepts": {
      const rows = present(await db.concepts.bulkGet(keys));
      if (rows.length > 0) await pushConcepts(userId, rows);
      break;
    }
    case "conceptForms": {
      const rows = present(await db.conceptForms.bulkGet(keys));
      if (rows.length > 0) await pushConceptForms(userId, rows);
      break;
    }
    case "reviewEvents": {
      const rows = present(await db.reviewEvents.bulkGet(keys));
      if (rows.length > 0) await pushReviewEvents(userId, rows);
      break;
    }
    case "cardStates": {
      const rows = present(await db.cardStates.bulkGet(keys));
      if (rows.length > 0) await pushCardStates(userId, rows);
      break;
    }
  }
}

async function deleteRemote(userId: string, table: OutboxTable, key: string): Promise<void> {
  switch (table) {
    case "decks":
      await deleteDeckRemote(userId, key);
      break;
    case "concepts":
      await deleteConceptRemote(userId, key);
      break;
    case "conceptForms":
      await deleteConceptFormRemote(userId, key);
      break;
    case "cardStates":
      await deleteCardStateRemote(userId, key);
      break;
  }
}

async function syncNow(): Promise<void> {
  const userId = currentUserId;
  await flush();
  if (userId && currentUserId === userId) await initialSync(userId);
}

// ---------- event listeners (browser only) ----------

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    syncNow().catch((err) => console.warn("[sync] online sync error:", err));
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      syncNow().catch((err) => console.warn("[sync] visibility sync error:", err));
    }
  });
}
